import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from "react";
import Select from "react-select";
import projectManager from "../models/projects";
import { api, getActiveWorkspaceId } from "../api/client";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { getPriorityColors } from "../utils/priorityColors";
const ProjectTracker = lazy(() => import("./ProjectTracker"));
const TeamManagementLazy = lazy(() => import("./TeamManagement"));

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1)  return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtDate(str) {
  if (!str) return "";
  return new Date(str).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function avatar(user, size = 34) {
  const initials = (user?.name || "?")
    .split(" ")
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: user?.color || "#0C2D48",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 700, fontSize: size * 0.38,
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// Build per-member task stats from raw tasks + users list
function buildMemberStats(tasks, users) {
  const now = new Date();
  const map = {};
  users.forEach(u => {
    map[u.id] = { user: u, total: 0, completed: 0, outstanding: 0, overdue: 0 };
  });
  tasks.forEach(task => {
    const ids = new Set([
      task.userId,
      task.createdBy,
      ...(Array.isArray(task.assignedTeam) ? task.assignedTeam : []),
    ].filter(Boolean));
    ids.forEach(id => {
      if (!map[id]) return;
      map[id].total++;
      if (task.completed) {
        map[id].completed++;
      } else {
        map[id].outstanding++;
        const deadline = task.deadline || task.dueDate;
        if (deadline && new Date(deadline) < now) map[id].overdue++;
      }
    });
  });
  return Object.values(map);
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const tabStyle = (active) => ({
  padding: "0.5rem 1.1rem", border: "none", background: "transparent",
  borderBottom: active ? "2px solid #0C2D48" : "2px solid transparent",
  fontWeight: active ? 700 : 500, fontSize: "0.9rem",
  color: active ? "#0C2D48" : "#555", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", marginBottom: "-2px", whiteSpace: "nowrap",
});

const card = {
  background: "#fff", border: "1px solid #35454C", borderRadius: 10,
  padding: "0.9rem 1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const chipStyle = (active, color) => ({
  padding: "0.25rem 0.75rem", borderRadius: 20, fontSize: "0.78rem", fontWeight: 600,
  fontFamily: "Raleway, sans-serif",
  cursor: "pointer", border: active ? `1px solid ${color || "#0C2D48"}` : "1px solid #c5cdd4",
  background: active ? (color ? color + "22" : "#e8f0fb") : "#f5f7f9",
  color: active ? (color || "#0C2D48") : "#35454C",
  transition: "all 0.15s",
});

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ stats, tasks }) {
  const [selectedMember, setSelectedMember] = useState(null);
  const now = new Date();

  // Week range (Sun–Sat of current week)
  const weekStart = useMemo(() => {
    const d = new Date(now); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const weekEnd = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 6); d.setHours(23,59,59,999); return d;
  }, [weekStart]);

  // Month range
  const monthStart = useMemo(() => new Date(now.getFullYear(), now.getMonth(), 1), []); // eslint-disable-line react-hooks/exhaustive-deps
  const monthEnd   = useMemo(() => new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999), []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => ({
    total:       tasks.length,
    completed:   tasks.filter(t => t.completed).length,
    outstanding: tasks.filter(t => !t.completed).length,
    overdue:     tasks.filter(t => !t.completed && (t.deadline || t.dueDate) && new Date(t.deadline || t.dueDate) < now).length,
  }), [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const leaderboard = useMemo(() =>
    [...stats].sort((a, b) => b.completed - a.completed).slice(0, 5), [stats]);
  const mostWork = useMemo(() =>
    [...stats].sort((a, b) => b.outstanding - a.outstanding).slice(0, 5), [stats]);
  const maxCompleted   = Math.max(...leaderboard.map(s => s.completed), 1);
  const maxOutstanding = Math.max(...mostWork.map(s => s.outstanding), 1);

  function getUserTasks(userId) {
    return tasks.filter(task => {
      const ids = new Set([task.userId, task.createdBy, ...(Array.isArray(task.assignedTeam) ? task.assignedTeam : [])].filter(Boolean));
      return ids.has(userId);
    });
  }

  function getTasksInRange(userTasks, start, end) {
    return userTasks.filter(task => {
      const dateStr = task.date || task.datetime || task.dueDate || task.deadline;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return !isNaN(d.getTime()) && d >= start && d <= end;
    });
  }

  function breakdown(list) {
    return {
      completed:   list.filter(t => t.completed).length,
      outstanding: list.filter(t => !t.completed).length,
      overdue:     list.filter(t => !t.completed && (t.deadline || t.dueDate) && new Date(t.deadline || t.dueDate) < now).length,
    };
  }

  // ── Member detail modal ────────────────────────────────────────────────────
  const MemberModal = () => {
    if (!selectedMember) return null;
    const { user } = selectedMember;
    const userTasks  = getUserTasks(user.id);
    const weekTasks  = getTasksInRange(userTasks, weekStart, weekEnd);
    const monthTasks = getTasksInRange(userTasks, monthStart, monthEnd);
    const allBreak   = breakdown(userTasks);
    const weekBreak  = breakdown(weekTasks);
    const monthBreak = breakdown(monthTasks);
    const pct        = userTasks.length > 0 ? Math.round(allBreak.completed / userTasks.length * 100) : 0;
    const teamAvgOut = stats.length > 0 ? totals.outstanding / stats.length : 0;
    const userOut    = allBreak.outstanding;
    const loadRatio  = teamAvgOut > 0 ? userOut / teamAvgOut : (userOut > 0 ? 2 : 0);
    const loadLabel  = loadRatio > 1.4 ? "Heavy" : loadRatio > 0.6 ? "Normal" : "Light";
    const loadColor  = loadRatio > 1.4 ? "#A12710" : loadRatio > 0.6 ? "#7a4a10" : "#1e6645";
    const loadBar    = loadRatio > 1.4 ? "#f5a5a5" : loadRatio > 0.6 ? "#FFA384" : "#E4E69B";

    return (
      <div className="modal-overlay" onClick={() => setSelectedMember(null)}>
        <div
          className="task-modal"
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: 480, width: "100%", border: "1px solid #35454C", fontFamily: "Raleway, sans-serif", padding: "1.75rem" }}
        >
          {/* Header */}
          <div className="modal-header" style={{ marginBottom: "1.25rem", paddingBottom: "1rem", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
              {avatar(user, 44)}
              <div>
                <div style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#0C2D48" }}>{user.name}</div>
                <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.75rem", color: "#888", textTransform: "capitalize" }}>{user.role?.replace(/_/g, " ")}</div>
              </div>
            </div>
            <button className="close-btn" onClick={() => setSelectedMember(null)}>✕</button>
          </div>

          {/* Overall progress bar */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "Raleway, sans-serif", fontSize: "0.77rem", color: "#888", marginBottom: "0.35rem" }}>
              <span>Overall completion</span>
              <span style={{ fontWeight: 700, color: "#0C2D48" }}>{pct}%</span>
            </div>
            <div style={{ height: 7, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#1e6645" : "#E4E69B", borderRadius: 4, transition: "width 0.4s" }} />
            </div>
          </div>

          {/* Week & Month breakdowns */}
          {[
            { label: "This week",  data: weekBreak,  total: weekTasks.length },
            { label: "This month", data: monthBreak, total: monthTasks.length },
          ].map(({ label, data, total }) => (
            <div key={label} style={{ marginBottom: "0.85rem", padding: "0.85rem 1rem", background: "#f9f9f9", borderRadius: 10, border: "1px solid #35454C" }}>
              <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#0C2D48", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.7rem" }}>{label}</div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {[
                  { l: "Completed",   v: data.completed,   c: "#1e6645" },
                  { l: "Outstanding", v: data.outstanding, c: "#7a4a10" },
                  { l: "Overdue",     v: data.overdue,     c: "#A12710" },
                  { l: "Total",       v: total,            c: "#0C2D48" },
                ].map(({ l, v, c }) => (
                  <div key={l} style={{ flex: 1, textAlign: "center", padding: "0.5rem 0.25rem", background: "#fff", borderRadius: 8, border: "1px solid #35454C" }}>
                    <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "1.35rem", fontWeight: 700, color: c, lineHeight: 1.1 }}>{v}</div>
                    <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.65rem", color: "#888", fontWeight: 600, marginTop: "0.15rem" }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Workload balance */}
          <div style={{ padding: "0.85rem 1rem", background: "#f9f9f9", borderRadius: 10, border: "1px solid #35454C" }}>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#0C2D48", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.65rem" }}>Workload balance</div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "Raleway, sans-serif", fontSize: "0.73rem", color: "#888", marginBottom: "0.3rem" }}>
                  <span>{userOut} outstanding tasks</span>
                  <span>team avg: {Math.round(teamAvgOut)}</span>
                </div>
                <div style={{ height: 7, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(loadRatio * 60, 100)}%`, background: loadBar, borderRadius: 4, transition: "width 0.4s" }} />
                </div>
              </div>
              <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.82rem", fontWeight: 700, color: loadColor, minWidth: 46, textAlign: "right" }}>{loadLabel}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      {/* Stats strip */}
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        {[
          { label: "Total tasks",  value: totals.total,       color: "#0C2D48" },
          { label: "Completed",    value: totals.completed,   color: "#1e6645" },
          { label: "Outstanding",  value: totals.outstanding, color: "#7a4a10" },
          { label: "Overdue",      value: totals.overdue,     color: "#A12710" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ ...card, flex: 1, minWidth: 120 }}>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#4a5a5b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.2rem" }}>{label}</div>
            <div style={{ fontSize: "1.5rem", fontWeight: 900, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Leaderboard + Workload — above member cards */}
      <div className="team-lb-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <div style={card}>
          <h4 style={{ fontFamily: "Poppins, sans-serif", margin: "0 0 0.75rem", fontSize: "0.85rem", fontWeight: 700, color: "#0C2D48" }}>🏆 Most completed</h4>
          {leaderboard.length === 0 ? (
            <div style={{ fontFamily: "Raleway, sans-serif", color: "#4a5a5b", fontSize: "0.85rem" }}>No completed tasks yet.</div>
          ) : leaderboard.map(({ user, completed }, i) => (
            <div key={user.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
              <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.75rem", color: i === 0 ? "#c8930a" : "#4a5a5b", fontWeight: 700, width: 16 }}>{i + 1}</span>
              {avatar(user, 26)}
              <span style={{ flex: 1, fontSize: "0.85rem", fontWeight: 600 }}>{user.name}</span>
              <div style={{ width: 80, height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(completed / maxCompleted) * 100}%`, background: "#E4E69B", borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#1e6645", minWidth: 24, textAlign: "right" }}>{completed}</span>
            </div>
          ))}
        </div>
        <div style={card}>
          <h4 style={{ fontFamily: "Poppins, sans-serif", margin: "0 0 0.75rem", fontSize: "0.85rem", fontWeight: 700, color: "#0C2D48" }}>📋 Most outstanding</h4>
          {mostWork.length === 0 ? (
            <div style={{ color: "#aaa", fontSize: "0.85rem" }}>All clear!</div>
          ) : mostWork.map(({ user, outstanding }, i) => (
            <div key={user.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
              <span style={{ fontSize: "0.75rem", color: "#aaa", fontWeight: 700, width: 16 }}>{i + 1}</span>
              {avatar(user, 26)}
              <span style={{ flex: 1, fontSize: "0.85rem", fontWeight: 600 }}>{user.name}</span>
              <div style={{ width: 80, height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(outstanding / maxOutstanding) * 100}%`, background: "#FFA384", borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#7a4a10", minWidth: 24, textAlign: "right" }}>{outstanding}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Member cards — clickable */}
      <div>
        <h4 style={{ fontFamily: "Poppins, sans-serif", margin: "0 0 0.75rem", fontSize: "0.85rem", fontWeight: 700, color: "#4a5a5b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Team members</h4>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "0.75rem" }}>
          {stats.map(({ user, total, completed, outstanding, overdue }) => {
            const pct = total > 0 ? Math.round(completed / total * 100) : 0;
            return (
              <div
                key={user.id}
                onClick={() => setSelectedMember({ user, total, completed, outstanding, overdue })}
                style={{ ...card, cursor: "pointer", transition: "box-shadow 0.15s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.65rem" }}>
                  {avatar(user, 36)}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "0.92rem", color: "#0C2D48" }}>{user.name}</div>
                    <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.72rem", color: "#4a5a5b" }}>{user.role?.replace("_", " ")}</div>
                  </div>
                  <span style={{ fontSize: "0.68rem", color: "#bbb" }}>View →</span>
                </div>
                <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden", marginBottom: "0.5rem" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#1e6645" : "#E4E69B", borderRadius: 3, transition: "width 0.4s" }} />
                </div>
                <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.75rem" }}>
                  <span style={{ color: "#1e6645", fontWeight: 700 }}>✓ {completed}</span>
                  <span style={{ color: "#7a4a10" }}>◎ {outstanding}</span>
                  {overdue > 0 && <span style={{ color: "#A12710", fontWeight: 700 }}>⚠ {overdue} overdue</span>}
                  <span style={{ color: "#aaa", marginLeft: "auto" }}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <MemberModal />
    </div>
  );
}

// ─── Task Board Tab ────────────────────────────────────────────────────────────
function TaskBoardTab({ tasks, users, onAddTask }) {
  const [memberFilter, setMemberFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTask, setSelectedTask] = useState(null);
  const [boardView, setBoardView]       = useState("list"); // 'list' | 'kanban' | 'byproject'
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const now = new Date();

  const projects = useMemo(() => {
    try { return projectManager.getAllProjects() || []; } catch { return []; }
  }, []);

  const filtered = useMemo(() => {
    return tasks.filter(task => {
      if (memberFilter !== null) {
        const ids = new Set([
          task.userId,
          task.createdBy,
          ...(Array.isArray(task.assignedTeam) ? task.assignedTeam : []),
        ].filter(Boolean));
        if (!ids.has(memberFilter)) return false;
      }
      if (statusFilter === "completed")   return task.completed;
      if (statusFilter === "outstanding") return !task.completed;
      if (statusFilter === "overdue") {
        const dl = task.deadline || task.dueDate;
        return !task.completed && dl && new Date(dl) < now;
      }
      return true;
    });
  }, [tasks, memberFilter, statusFilter, now]);

  // Kanban columns
  const kanbanCols = useMemo(() => [
    {
      label: "To Do", color: "#7a4a10", bg: "#fff8ee",
      tasks: filtered.filter(t => !t.completed && !((t.deadline || t.dueDate) && new Date(t.deadline || t.dueDate) < now)),
    },
    {
      label: "Overdue", color: "#A12710", bg: "#fde8e8",
      tasks: filtered.filter(t => !t.completed && (t.deadline || t.dueDate) && new Date(t.deadline || t.dueDate) < now),
    },
    {
      label: "Done", color: "#1e6645", bg: "#eaf6ef",
      tasks: filtered.filter(t => t.completed),
    },
  ], [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // By-project groups
  const byProjectGroups = useMemo(() => {
    const groups = {};
    filtered.forEach(task => {
      const key = task.projectId ? String(task.projectId) : "none";
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return Object.entries(groups)
      .map(([key, groupTasks]) => {
        const project = key !== "none" ? projects.find(p => String(p.id) === key) : null;
        return {
          key,
          name: project?.name || (key !== "none" ? `Project ${key}` : "No Project"),
          tasks: groupTasks,
        };
      })
      .sort((a, b) => a.name === "No Project" ? 1 : b.name === "No Project" ? -1 : a.name.localeCompare(b.name));
  }, [filtered, projects]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  function taskMembers(task) {
    const ids = new Set([
      task.userId,
      task.createdBy,
      ...(Array.isArray(task.assignedTeam) ? task.assignedTeam : []),
    ].filter(Boolean));
    return [...ids].map(id => users.find(u => u.id === id)).filter(Boolean);
  }

  const statusColor = (task) => {
    if (task.completed) return { bg: "#eaf6ef", text: "#1e6645", label: "Done" };
    const dl = task.deadline || task.dueDate;
    if (dl && new Date(dl) < now) return { bg: "#fde8e8", text: "#A12710", label: "Overdue" };
    return { bg: "#fff8ee", text: "#7a4a10", label: "Pending" };
  };

  // Shared compact task card for kanban + by-project views
  const CompactCard = ({ task }) => {
    const members = taskMembers(task);
    return (
      <div
        onClick={() => setSelectedTask(task)}
        style={{
          background: "#fff", borderRadius: 8, padding: "0.6rem 0.75rem", cursor: "pointer",
          border: "1px solid #e8eaed", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.1)"; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)"; }}
      >
        <div style={{
          fontWeight: 600, fontSize: "0.83rem",
          color: task.completed ? "#aaa" : "#1a1a1a",
          textDecoration: task.completed ? "line-through" : "none",
          marginBottom: "0.35rem", lineHeight: 1.3,
        }}>
          {task.title}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {(task.deadline || task.dueDate) && (
            <span style={{ fontSize: "0.7rem", color: "#aaa" }}>{fmtDate(task.deadline || task.dueDate)}</span>
          )}
          <div style={{ display: "flex", gap: "0.15rem", marginLeft: "auto" }}>
            {members.slice(0, 3).map(u => (
              <div key={u.id} title={u.name}>{avatar(u, 20)}</div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.9rem", alignItems: "center" }}>
        <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.78rem", color: "#35454C", fontWeight: 700, letterSpacing: "0.03em" }}>Member:</span>
        <div style={{ width: 200 }}>
          <Select
            value={memberFilter ? { value: memberFilter, label: users.find(u => u.id === memberFilter)?.name?.split(" ")[0] || "" } : null}
            onChange={(option) => setMemberFilter(option ? option.value : null)}
            options={users.map(u => ({ value: u.id, label: u.name.split(" ")[0] }))}
            placeholder="All members"
            isClearable={true}
            isSearchable={true}
            styles={{
              control: (provided) => ({
                ...provided, minHeight: "32px", fontSize: "0.8rem",
                border: "1px solid #e0e0e0", borderRadius: "20px", cursor: "pointer",
                boxShadow: "none", "&:hover": { borderColor: "#0C2D48" },
              }),
              valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
              indicatorsContainer: (provided) => ({ ...provided, height: "32px" }),
              indicatorSeparator: () => ({ display: "none" }),
              placeholder: (provided) => ({ ...provided, fontSize: "0.8rem", color: "#888" }),
              singleValue: (provided) => ({ ...provided, fontSize: "0.8rem", fontWeight: 600 }),
              option: (provided, state) => ({ ...provided, fontSize: "0.82rem", padding: "0.4rem 0.75rem", background: state.isSelected ? "#0C2D48" : state.isFocused ? "#f0f0f0" : "white", color: state.isSelected ? "#fff" : "#333" }),
              menu: (provided) => ({ ...provided, zIndex: 9999, borderRadius: "8px" }),
            }}
          />
        </div>
        <span style={{ fontFamily: "Poppins, sans-serif", marginLeft: "0.5rem", fontSize: "0.78rem", color: "#35454C", fontWeight: 700, letterSpacing: "0.03em" }}>Status:</span>
        {[["all", "All"], ["outstanding", "Outstanding"], ["completed", "Completed"], ["overdue", "Overdue"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} style={chipStyle(statusFilter === v, "#0C2D48")}>{l}</button>
        ))}

        {/* View toggle + Add Task */}
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.25rem", alignItems: "center", flexWrap: "wrap" }}>
          {[["list", "☰ List"], ["kanban", "⬛ Kanban"], ["byproject", "📁 By Project"]].map(([v, l]) => (
            <button key={v} onClick={() => setBoardView(v)} style={{
              padding: "0.28rem 0.65rem", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600,
              cursor: "pointer", border: `1px solid ${boardView === v ? "#0C2D48" : "#e0e3e8"}`,
              background: boardView === v ? "#0C2D48" : "#fff",
              color: boardView === v ? "#fff" : "#666",
              fontFamily: "Raleway, sans-serif",
            }}>{l}</button>
          ))}
          {onAddTask && (
            <button onClick={onAddTask} style={{
              marginLeft: "0.4rem", padding: "0.32rem 0.9rem", borderRadius: 7, border: "none",
              background: "#E4E69B", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
              fontFamily: "Raleway, sans-serif", color: "#0C2D48",
            }}>
              + Add Task
            </button>
          )}
        </div>
      </div>

      {/* Task detail modal */}
      {selectedTask && (() => {
        const task = selectedTask;
        const st = statusColor(task);
        const members = taskMembers(task);
        const dl = task.deadline || task.dueDate;
        return (
          <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
            <div
              className="task-modal"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 500, width: "100%", border: "1px solid #35454C", fontFamily: "Raleway, sans-serif", padding: "1.75rem" }}
            >
              <div className="modal-header" style={{ marginBottom: "1.25rem", paddingBottom: "1rem", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#0C2D48", flex: 1, paddingRight: "1rem" }}>
                  {task.title}
                </div>
                <button className="close-btn" onClick={() => setSelectedTask(null)}>✕</button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "0.2rem 0.6rem", borderRadius: 4, background: st.bg, color: st.text, fontFamily: "Poppins, sans-serif" }}>{st.label}</span>
                  {task.priority && (
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "0.2rem 0.6rem", borderRadius: 4, background: getPriorityColors(task.priority).bg, color: getPriorityColors(task.priority).text, fontFamily: "Poppins, sans-serif" }}>{task.priority}</span>
                  )}
                  {task.taskType && (
                    <span style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem", borderRadius: 4, background: "#f3f4f6", color: "#555", fontFamily: "Raleway, sans-serif" }}>{task.taskType}</span>
                  )}
                </div>

                {dl && (
                  <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.85rem", color: "#35454C", alignItems: "center" }}>
                    <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#0C2D48", minWidth: 80 }}>Due</span>
                    <span style={{ color: !task.completed && new Date(dl) < now ? "#A12710" : "#35454C" }}>{fmtDate(dl)}</span>
                  </div>
                )}

                {task.description && (
                  <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.85rem", color: "#35454C", alignItems: "flex-start" }}>
                    <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#0C2D48", minWidth: 80, paddingTop: "0.1rem" }}>Notes</span>
                    <span style={{ fontFamily: "Raleway, sans-serif", lineHeight: 1.5, flex: 1 }}>{task.description}</span>
                  </div>
                )}

                {members.length > 0 && (
                  <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.85rem", color: "#35454C", alignItems: "center" }}>
                    <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, color: "#0C2D48", minWidth: 80 }}>Assigned</span>
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                      {members.map(u => (
                        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                          {avatar(u, 22)}
                          <span style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.82rem" }}>{u.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── List view ── */}
      {boardView === "list" && (
        filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2.5rem", color: "#aaa", fontSize: "0.9rem" }}>No tasks match this filter.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            {filtered.map(task => {
              const st = statusColor(task);
              const members = taskMembers(task);
              return (
                <div key={task.id} onClick={() => setSelectedTask(task)} style={{
                  ...card, display: "flex", alignItems: "center", gap: "0.9rem",
                  padding: "0.7rem 1rem", flexWrap: "wrap", cursor: "pointer",
                  borderLeft: `3px solid ${task.completed ? "#6abf85" : (task.deadline && new Date(task.deadline) < now ? "#f5a5a5" : "#e5e7eb")}`,
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; }}
                >
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem", color: task.completed ? "#aaa" : "#242629", textDecoration: task.completed ? "line-through" : "none" }}>
                      {task.title}
                    </div>
                    {(task.datetime || task.dueDate) && (
                      <div style={{ fontSize: "0.74rem", color: "#aaa", marginTop: "0.1rem" }}>
                        {task.datetime ? fmtDate(task.datetime) : fmtDate(task.dueDate)}
                        {task.taskType && <span style={{ marginLeft: "0.4rem", background: "#f3f3f3", padding: "0 0.3rem", borderRadius: 4 }}>{task.taskType}</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "0.2rem" }}>
                    {members.slice(0, 4).map(u => (
                      <div key={u.id} title={u.name}>{avatar(u, 24)}</div>
                    ))}
                    {members.length > 4 && <div style={{ ...card, width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", color: "#888", padding: 0 }}>+{members.length - 4}</div>}
                  </div>
                  {task.priority && (
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "0.15rem 0.5rem", borderRadius: 4, background: getPriorityColors(task.priority).bg, color: getPriorityColors(task.priority).text }}>
                      {task.priority}
                    </span>
                  )}
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "0.15rem 0.5rem", borderRadius: 4, background: st.bg, color: st.text }}>
                    {st.label}
                  </span>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Kanban view ── */}
      {boardView === "kanban" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem", alignItems: "start" }}>
          {kanbanCols.map(col => (
            <div key={col.label} style={{ background: "#f5f7f9", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "0.65rem 1rem", background: col.bg, borderBottom: `2px solid ${col.color}30`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "0.82rem", color: col.color }}>{col.label}</span>
                <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.72rem", fontWeight: 700, color: col.color, background: "#fff", borderRadius: 10, padding: "0.1rem 0.5rem", border: `1px solid ${col.color}30` }}>{col.tasks.length}</span>
              </div>
              <div style={{ padding: "0.5rem", display: "flex", flexDirection: "column", gap: "0.4rem", minHeight: 80 }}>
                {col.tasks.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "1.25rem 0.5rem", color: "#ccc", fontSize: "0.78rem" }}>Empty</div>
                ) : col.tasks.map(task => <CompactCard key={task.id} task={task} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── By Project view ── */}
      {boardView === "byproject" && (
        byProjectGroups.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2.5rem", color: "#aaa", fontSize: "0.9rem" }}>No tasks match this filter.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {byProjectGroups.map(({ key, name, tasks: groupTasks }) => {
              const collapsed = collapsedGroups.has(key);
              return (
                <div key={key} style={{ border: "1px solid #e8eaed", borderRadius: 10, overflow: "hidden" }}>
                  <button
                    onClick={() => toggleGroup(key)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "0.65rem 1rem", background: "#f5f7f9", border: "none", cursor: "pointer",
                      borderBottom: collapsed ? "none" : "1px solid #e8eaed",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#0C2D48" }}>📁 {name}</span>
                      <span style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.72rem", fontWeight: 700, color: "#888", background: "#fff", borderRadius: 10, padding: "0.1rem 0.5rem", border: "1px solid #e0e3e8" }}>{groupTasks.length}</span>
                    </div>
                    <span style={{ fontSize: "0.75rem", color: "#aaa" }}>{collapsed ? "▶" : "▼"}</span>
                  </button>
                  {!collapsed && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", padding: "0.5rem" }}>
                      {groupTasks.map(task => {
                        const st = statusColor(task);
                        const members = taskMembers(task);
                        return (
                          <div key={task.id} onClick={() => setSelectedTask(task)} style={{
                            ...card, display: "flex", alignItems: "center", gap: "0.9rem",
                            padding: "0.65rem 1rem", flexWrap: "wrap", cursor: "pointer",
                            borderLeft: `3px solid ${task.completed ? "#6abf85" : ((task.deadline || task.dueDate) && new Date(task.deadline || task.dueDate) < now ? "#f5a5a5" : "#e5e7eb")}`,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
                          onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; }}
                          >
                            <div style={{ flex: 1, minWidth: 140 }}>
                              <div style={{ fontWeight: 600, fontSize: "0.86rem", color: task.completed ? "#aaa" : "#242629", textDecoration: task.completed ? "line-through" : "none" }}>{task.title}</div>
                              {(task.datetime || task.dueDate) && (
                                <div style={{ fontSize: "0.72rem", color: "#aaa", marginTop: "0.1rem" }}>
                                  {fmtDate(task.datetime || task.dueDate)}
                                  {task.taskType && <span style={{ marginLeft: "0.4rem", background: "#f3f3f3", padding: "0 0.3rem", borderRadius: 4 }}>{task.taskType}</span>}
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: "0.2rem" }}>
                              {members.slice(0, 3).map(u => <div key={u.id} title={u.name}>{avatar(u, 22)}</div>)}
                            </div>
                            <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "0.15rem 0.5rem", borderRadius: 4, background: st.bg, color: st.text }}>{st.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ─── Team Chat Tab ────────────────────────────────────────────────────────────
// Keyed by the active (business) workspace so the local chat store never carries
// across a workspace switch. TeamDashboard is business-only, so this is belt-and-
// suspenders, but it keeps to the workspace-scoping principle.
function teamChatKey() {
  return `dashboard_team_chat_${getActiveWorkspaceId() || 'none'}`;
}

function TeamChatTab({ currentUser, users }) {
  const [chatTab, setChatTab] = useState("all");
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(teamChatKey()) || "{}"); } catch { return {}; }
  });
  const [newMessage, setNewMessage] = useState("");
  const bottomRef = useRef(null);

  // Poll for updates from Dashboard chat or other tabs
  useEffect(() => {
    const interval = setInterval(() => {
      try { setMessages(JSON.parse(localStorage.getItem(teamChatKey()) || "{}")); } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatTab]);

  const handleSend = useCallback(() => {
    if (!newMessage.trim() || !currentUser) return;
    setMessages(prev => {
      const updated = {
        ...prev,
        [chatTab]: [...(prev[chatTab] || []), {
          from: currentUser.name,
          text: newMessage.trim(),
          timestamp: Date.now(),
        }],
      };
      try { localStorage.setItem(teamChatKey(), JSON.stringify(updated)); } catch {}
      return updated;
    });
    setNewMessage("");
  }, [newMessage, chatTab, currentUser]);

  const formatMessageTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const teamUsers = users.filter(u => u.id !== currentUser?.id);

  return (
    <div>
      {/* Member tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", overflowX: "auto", paddingBottom: "0.25rem" }}>
        <button
          onClick={() => setChatTab("all")}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            padding: "0.5rem 1rem",
            backgroundColor: chatTab === "all" ? "#e4e69b" : "#f3f3f3",
            color: chatTab === "all" ? "#212121" : "#495057",
            border: "none", borderRadius: "6px", cursor: "pointer",
            fontSize: "0.9rem", fontWeight: "600", whiteSpace: "nowrap",
            fontFamily: "Raleway, sans-serif",
          }}
        >
          👥 All Team
        </button>
        {teamUsers.map(user => (
          <button
            key={user.id}
            onClick={() => setChatTab(user.id)}
            style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              padding: "0.5rem 1rem",
              backgroundColor: chatTab === user.id ? "#e4e69b" : "#f3f3f3",
              color: chatTab === user.id ? "#212121" : "#495057",
              border: "none", borderRadius: "6px", cursor: "pointer",
              fontSize: "0.9rem", fontWeight: "500", whiteSpace: "nowrap",
              fontFamily: "Raleway, sans-serif",
            }}
          >
            <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: user.color || "#007acc", flexShrink: 0 }} />
            {user.name}
          </button>
        ))}
      </div>

      {teamUsers.length === 0 ? (
        <div style={{ padding: "3rem", backgroundColor: "#f8f9fa", borderRadius: "12px", textAlign: "center", border: "2px dashed #dee2e6" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>👥</div>
          <div style={{ fontSize: "1.2rem", fontWeight: "600", color: "#495057", marginBottom: "0.5rem", fontFamily: "Poppins, sans-serif" }}>No Team Members</div>
          <div style={{ color: "#6c757d", fontSize: "1rem", fontFamily: "Raleway, sans-serif" }}>Add more users to start team conversations</div>
        </div>
      ) : (
        <>
          {/* Messages */}
          <div style={{
            maxHeight: "340px", overflowY: "auto", backgroundColor: "#fff",
            padding: "1rem", borderRadius: "8px", border: "1px solid #dee2e6",
            marginBottom: "1rem", minHeight: "200px",
          }}>
            {(messages[chatTab] || []).length === 0 ? (
              <div style={{ textAlign: "center", color: "#6c757d", padding: "2rem", fontSize: "1rem", fontFamily: "Raleway, sans-serif" }}>
                {chatTab === "all"
                  ? "💬 No messages yet. Say something to the whole team!"
                  : `💬 No messages yet. Start the conversation with ${teamUsers.find(u => u.id === chatTab)?.name || "your teammate"}!`}
              </div>
            ) : (
              (messages[chatTab] || []).map((msg, idx) => (
                <div
                  key={msg.id || `${msg.timestamp}-${idx}`}
                  style={{
                    marginBottom: "0.75rem", padding: "0.5rem 0",
                    borderBottom: idx < (messages[chatTab] || []).length - 1 ? "1px solid #f1f3f4" : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                    <strong style={{ color: "#495057", fontFamily: "Raleway, sans-serif" }}>{msg.from}:</strong>
                    {msg.timestamp && (
                      <span style={{ fontSize: "0.75rem", color: "#6c757d" }}>{formatMessageTime(msg.timestamp)}</span>
                    )}
                  </div>
                  <div style={{ color: "#333", lineHeight: "1.4", fontFamily: "Raleway, sans-serif" }}>{msg.text}</div>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
              placeholder={chatTab === "all" ? "Send a message to the whole team..." : `Type a message to ${users.find(u => u.id === chatTab)?.name}...`}
              style={{
                flex: 1, padding: "0.75rem", borderRadius: "6px",
                border: "1px solid #ced4da", fontSize: "0.95rem",
                outline: "none", fontFamily: "Raleway, sans-serif",
              }}
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim()}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor: newMessage.trim() ? "#82b3cc" : "#dee2e6",
                color: newMessage.trim() ? "#fff" : "#6c757d",
                border: "none", borderRadius: "6px",
                cursor: newMessage.trim() ? "pointer" : "not-allowed",
                fontSize: "0.95rem", fontWeight: "600", fontFamily: "Raleway, sans-serif",
              }}
            >
              Send
            </button>
          </div>
          <div style={{ fontSize: "0.75rem", color: "#6c757d", textAlign: "center", marginTop: "0.5rem", fontFamily: "Raleway, sans-serif" }}>
            Press Enter to send
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main TeamDashboard ────────────────────────────────────────────────────────
function TeamDashboard({ onAddTask }) {
  const { currentUser, isTeamAdmin } = useCurrentUser();
  const [activeTab, setActiveTab] = useState("Overview");
  const [tasks,     setTasks]     = useState([]);
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadTeamData = () => {
    return Promise.all([
      api('/api/team/members'),
      api('/api/tasks?team=true'),
    ]);
  };

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    loadTeamData()
      .then(async ([membersRes, tasksRes]) => {
        if (!membersRes.ok || !tasksRes.ok) throw new Error('Failed to load team data');
        const membersData = await membersRes.json();
        const tasksData = await tasksRes.json();
        setUsers((membersData.members || []).filter(u => u.status !== "inactive"));
        setTasks(tasksData.tasks || []);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  // Refresh tasks when switching to Task Board
  useEffect(() => {
    if (activeTab === "Task Board") {
      api('/api/tasks?team=true')
        .then(async res => {
          if (!res.ok) return;
          const data = await res.json();
          setTasks(data.tasks || []);
        })
        .catch(() => {});
    }
  }, [activeTab]);

  const stats = useMemo(() => buildMemberStats(tasks, users), [tasks, users]);

  const TABS = ["Overview", "Task Board", "Projects", "Team Chat", ...(isTeamAdmin ? ["Manage Team"] : [])];

  if (loading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "#999", fontFamily: "Raleway, sans-serif" }}>Loading…</div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "#9a3412", fontFamily: "Raleway, sans-serif" }}>
        Couldn't load team data. Please try refreshing the page.
      </div>
    );
  }

  return (
    <div style={{ padding: "1rem", maxWidth: 1400, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <div>
            <h2 style={{ fontFamily: "Poppins, sans-serif", margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#0C2D48" }}>Team</h2>
            <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "0.82rem", color: "#4a5a5b", marginTop: "0.1rem" }}>
              {users.length} member{users.length !== 1 ? "s" : ""} · {tasks.filter(t => !t.completed).length} outstanding tasks
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "2px solid #c5cdd4", marginBottom: "1.25rem", gap: 0, overflowX: "auto" }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={tabStyle(activeTab === tab)}>
              {tab === "Overview"    && "📊 "}
              {tab === "Task Board"  && "📋 "}
              {tab === "Projects"    && "🗂 "}
              {tab === "Team Chat"   && "💬 "}
              {tab === "Manage Team" && "👥 "}
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "Overview" && (
          <OverviewTab stats={stats} tasks={tasks} />
        )}

        {activeTab === "Task Board" && (
          <TaskBoardTab tasks={tasks} users={users} onAddTask={onAddTask} />
        )}

        {activeTab === "Projects" && (
          <Suspense fallback={<div style={{ padding: "2rem", color: "#aaa" }}>Loading projects…</div>}>
            <ProjectTracker />
          </Suspense>
        )}

        {activeTab === "Team Chat" && (
          <TeamChatTab currentUser={currentUser} users={users} />
        )}

        {activeTab === "Manage Team" && (
          <Suspense fallback={<div style={{ padding: "2rem", color: "#aaa" }}>Loading…</div>}>
            <TeamManagementLazy />
          </Suspense>
        )}
    </div>
  );
}

export default TeamDashboard;
